import { apiClient } from './api';
import {
  StartUXDesignSessionRequest,
  StartUXDesignSessionResponse,
  UXDesignIterationRequest,
  UXDesignIterationResponse,
  UXDesignBulkGenerateRequest,
  UXDesignBulkGenerateResponse,
  UXDesignSessionResponse,
  UXArtifact,
  UXDesignSession
} from '../models/uxDesign';

const API_PREFIX = '/api/uxdesign';
const STORAGE_KEY = 'uxdesign_sessions';

export class UXDesignService {
  // API methods
  static async startSession(request: StartUXDesignSessionRequest): Promise<StartUXDesignSessionResponse> {
    const response = await apiClient.post(`${API_PREFIX}/start`, request);
    return response.data;
  }

  static async generateArtifact(sessionId: string, request: UXDesignIterationRequest): Promise<UXDesignIterationResponse> {
    // Map frontend artifact types to backend enum values - try exact string matches first
    const typeMapping: Record<string, string> = {
      'persona': 'persona',
      'userJourney': 'userJourney',
      'userFlow': 'userFlow',
      'wireframe': 'wireframe',
      'mockup': 'mockup',
      'storyboard': 'storyboard',
      'prototype': 'prototype',
      'designSystem': 'designSystem',
      'usabilityReport': 'usabilityReport'
    };
    
    // Structure the request as expected by the backend API
    const payload = {
      sessionId: sessionId,
      Parameters: {
        artifactType: typeMapping[request.artifactType] || request.artifactType,
        fidelityLevel: request.fidelity || 'medium',
        temperature: request.temperature,
        maxTokens: request.maxTokens
      },
      SpecificRequirements: request.specificRequirements ? request.specificRequirements.toString() : ''
    };
    
    console.log('UX Design API Payload:', payload); // Debug log
    const response = await apiClient.post(`${API_PREFIX}/${sessionId}/iterate`, payload);
    return response.data;
  }

  static async bulkGenerateArtifacts(sessionId: string, request: UXDesignBulkGenerateRequest): Promise<UXDesignBulkGenerateResponse> {
    // Map frontend artifact types to backend enum values - try exact string matches first
    const typeMapping: Record<string, string> = {
      'persona': 'persona',
      'userJourney': 'userJourney',
      'userFlow': 'userFlow',
      'wireframe': 'wireframe',
      'mockup': 'mockup',
      'storyboard': 'storyboard',
      'prototype': 'prototype',
      'designSystem': 'designSystem',
      'usabilityReport': 'usabilityReport'
    };
    
    // Structure the request as expected by the backend API
    const payload = {
      sessionId: sessionId,
      Parameters: {
        artifactTypes: request.artifactTypes.map(type => typeMapping[type] || type),
        fidelityLevel: request.fidelity || 'medium',
        temperature: request.temperature,
        maxTokens: request.maxTokens
      },
      SpecificRequirements: request.specificRequirements ? request.specificRequirements.toString() : ''
    };
    
    console.log('UX Design Bulk API Payload:', payload); // Debug log
    const response = await apiClient.post(`${API_PREFIX}/${sessionId}/bulk-generate`, payload);
    return response.data;
  }

  static async getSession(sessionId: string): Promise<UXDesignSessionResponse> {
    const response = await apiClient.get(`${API_PREFIX}/${sessionId}`);
    return response.data;
  }

  static async getArtifacts(sessionId: string): Promise<UXArtifact[]> {
    const response = await apiClient.get(`${API_PREFIX}/${sessionId}/artifacts`);
    return response.data;
  }

  static async exportSession(sessionId: string): Promise<UXDesignSession> {
    const response = await apiClient.get(`${API_PREFIX}/${sessionId}/export`);
    return response.data;
  }

  // Local storage methods
  static saveSessionToLocal(session: UXDesignSession): void {
    const sessions = this.getAllLocalSessions();
    const index = sessions.findIndex(s => s.sessionId === session.sessionId);
    
    if (index !== -1) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  static getLocalSession(sessionId: string): UXDesignSession | null {
    const sessions = this.getAllLocalSessions();
    return sessions.find(s => s.sessionId === sessionId) || null;
  }

  static getAllLocalSessions(): UXDesignSession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static deleteLocalSession(sessionId: string): void {
    const sessions = this.getAllLocalSessions();
    const filtered = sessions.filter(s => s.sessionId !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }

  static clearAllLocalSessions(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  // Helper methods
  static exportSessionAsJson(session: UXDesignSession): string {
    return JSON.stringify(session, null, 2);
  }

  static downloadSession(session: UXDesignSession): void {
    const json = this.exportSessionAsJson(session);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ux-design-${session.projectName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static importSession(jsonString: string): UXDesignSession {
    return JSON.parse(jsonString);
  }

  // Utility methods for artifact types
  static getArtifactTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      'persona': 'User Persona',
      'userJourney': 'User Journey Map',
      'userFlow': 'User Flow Diagram',
      'wireframe': 'Wireframe',
      'mockup': 'Visual Mockup',
      'storyboard': 'Storyboard',
      'prototype': 'Interactive Prototype',
      'designSystem': 'Design System',
      'usabilityReport': 'Usability Report'
    };
    return labels[type] || type;
  }

  static getArtifactTypeDescription(type: string): string {
    const descriptions: Record<string, string> = {
      'persona': 'Detailed profiles of target users including demographics, goals, and behaviors',
      'userJourney': 'Visual representation of user experience across touchpoints',
      'userFlow': 'Step-by-step diagram showing user navigation paths',
      'wireframe': 'Low to medium fidelity structural blueprints of interfaces',
      'mockup': 'High fidelity visual designs with colors, typography, and imagery',
      'storyboard': 'Narrative sequences illustrating user scenarios',
      'prototype': 'Interactive demonstration of functionality and flow',
      'designSystem': 'Comprehensive guidelines for consistent design',
      'usabilityReport': 'Analysis of user testing results and recommendations'
    };
    return descriptions[type] || '';
  }

  static getRecommendedArtifactOrder(): string[] {
    return [
      'persona',
      'userJourney',
      'userFlow',
      'wireframe',
      'mockup',
      'storyboard',
      'prototype',
      'designSystem',
      'usabilityReport'
    ];
  }
}